import { delay } from '@olivierpascal/helpers';
import { Snackbar } from '@sixui/ui';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line import/no-unresolved
import { useRegisterSW } from 'virtual:pwa-register/react';

import { ITimeUnit } from '~/helpers/types';

const PWA_UPDATE_CHECK_INTERVAL = 5 * ITimeUnit.MINUTE;

export const PwaReloadPrompt: React.FC = () => {
  const { t } = useTranslation();
  const {
    offlineReady: [, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: false,
    onRegisteredSW(swUrl, registration) {
      if (!registration) {
        return;
      }

      setInterval(() => {
        if (registration.installing ?? !navigator) {
          return;
        }

        if ('econnection' in navigator && !navigator.onLine) {
          return;
        }

        fetch(swUrl, {
          cache: 'no-store',
          headers: {
            cache: 'no-store',
            'cache-control': 'no-cache',
          },
        })
          .then(() => registration.update())
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('SW update error:', error);
          });
      }, PWA_UPDATE_CHECK_INTERVAL);
    },
    onRegisterError(error) {
      // eslint-disable-next-line no-console
      console.error('SW registration error:', error);
    },
  });

  const close = (): void => {
    setOfflineReady(false);
    setNeedRefresh(false);
  };

  const handleUpdate = (): Promise<void> =>
    updateServiceWorker()
      .then(() => delay(ITimeUnit.SECOND))
      .then(() => window.location.reload());

  return (
    <Snackbar
      open={needRefresh}
      actionLabel={t('PwaReloadPrompt.update')}
      onActionClick={handleUpdate}
      onClose={() => close()}
    >
      {t('PwaReloadPrompt.updateAvailable')}
    </Snackbar>
  );
};
